import { type ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { type CoreMessageType } from "@sendbird/uikit-react/types/utils";
import { isEmpty } from "lodash";

import { logError } from "../lib/analytics";
import { type ShiftTimeProposalStatus } from "../Shifts/ShiftTimeProposals/types";
import { CustomMessageTypes, type ShiftInviteMessageProps } from "./type";

function hasAgentString(message: unknown): message is { agent: string } {
  return typeof message === "object" && isDefined(message) && "agent" in message;
}

function hasShiftInviteData(
  data: unknown
): data is { shiftInviteId: string; status: ShiftInviteStatus } {
  return typeof data === "object" && isDefined(data) && "shiftInviteId" in data && "status" in data;
}

export function parseAgentChatMessage(message: CoreMessageType) {
  try {
    const hasMessage = "message" in message && typeof message.message === "string";
    if (!hasMessage) {
      return undefined;
    }

    if (isValidJson(message.message)) {
      const parsedMessage: unknown = JSON.parse(message.message);
      return hasAgentString(parsedMessage) ? parsedMessage.agent : undefined;
    }

    return message.message;
  } catch (error) {
    logError("Failed to parse agent message from chat message", {
      error,
      metadata: { message },
    });
    return undefined;
  }
}

export function parseShiftInviteChatMessage(props: ShiftInviteMessageProps) {
  const { message } = props;
  const defaultData = { shiftInviteId: undefined, status: undefined };

  try {
    const agentMessage = parseAgentChatMessage(message);

    const hasData = isDefined(message.data) && !isEmpty(message.data);
    if (!hasData || !isValidJson(message.data)) {
      return { agentMessage, ...defaultData };
    }

    const parsedData: unknown = JSON.parse(message.data);
    if (!hasShiftInviteData(parsedData)) {
      return { agentMessage, ...defaultData };
    }

    return { agentMessage, ...parsedData };
  } catch (error) {
    logError("Shift Invite Chat Message Parsing Error", { error, metadata: { message } });
    return { agentMessage: undefined, ...defaultData };
  }
}

export function shiftTimeProposalDisplayMessage(chatType: CustomMessageTypes) {
  if (
    [
      CustomMessageTypes.SHIFT_TIME_PROPOSAL_CREATED,
      CustomMessageTypes.SHIFT_TIME_PROPOSAL_UPDATED,
    ].includes(chatType)
  ) {
    return "Proposed a new time for your shift";
  }

  if (chatType === CustomMessageTypes.SHIFT_TIME_PROPOSAL_REPLIED) {
    return "You replied to the shift time proposal";
  }

  return undefined;
}

interface GetShiftTimeProposalChatButtonLabelProps {
  status: ShiftTimeProposalStatus;
  isProposalExpired: boolean;
}

export function getShiftTimeProposalChatButtonLabel({
  status,
  isProposalExpired,
}: GetShiftTimeProposalChatButtonLabelProps) {
  if (status === "PROPOSED" && isProposalExpired) {
    return "Expired";
  }

  switch (status) {
    case "ACCEPTED":
    case "APPLIED": {
      return "Accepted!";
    }

    case "DECLINED": {
      return "Declined";
    }

    default: {
      return "Unavailable";
    }
  }
}

export function isValidJson(message: string) {
  try {
    JSON.parse(message);
    return true;
  } catch {
    return false;
  }
}
